import { useLayoutEffect, useRef, useState } from "react";
import styles from "./RetentionChart.module.scss";
import { retentionAt, steepestDrop, type RetentionChartPoint } from "./retention.js";


export interface RetentionChartProps {
  points: ReadonlyArray<RetentionChartPoint>;
  /** Video length in seconds. Without it the x-axis reads in percent of the video instead of a clock. */
  durationSeconds?: number | null;
  height?: number;
}

/**
 * Facebook's audience-retention curve: the share of viewers still watching through the video.
 *
 * The y-axis is FIXED at 0–100%, never fitted to the data. A fitted axis turns a curve that keeps 80% of its
 * audience into a cliff, which is the one wrong conclusion this chart can lead someone to. One series, so no legend
 * — the card title names it — and the steepest drop is marked because that is the moment worth looking at. A table
 * carries the same numbers for a screen reader.
 */
export function RetentionChart({ points, durationSeconds, height = 140 }: RetentionChartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const data = [...points].filter((p) => Number.isFinite(p.position) && Number.isFinite(p.retained))
    .sort((a, b) => a.position - b.position);
  if (data.length < 2) return null;

  const padL = 34;
  const padR = 8;
  const padT = 8;
  const padB = 20;
  const plotW = Math.max(width - padL - padR, 1);
  const plotH = height - padT - padB;
  const x = (p: number) => padL + p * plotW;
  const y = (r: number) => padT + (1 - r) * plotH;

  const line = data.map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.position).toFixed(1)} ${y(p.retained).toFixed(1)}`).join(" ");
  const area = `${line} L ${x(data[data.length - 1].position).toFixed(1)} ${y(0).toFixed(1)} L ${x(data[0].position).toFixed(1)} ${y(0).toFixed(1)} Z`;
  const drop = steepestDrop(data);
  const hovered = hover != null ? data[hover] : null;

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / Math.max(rect.width, 1);
    let nearest = 0;
    for (let i = 1; i < data.length; i++)
      if (Math.abs(data[i].position - pos) < Math.abs(data[nearest].position - pos)) nearest = i;
    setHover(nearest);
  };

  return (
    <div className={styles.host} ref={hostRef}>
      {width > 0 && (
        <svg width={width} height={height} role="img"
             aria-label={`Audience retention: ${Math.round(data[data.length - 1].retained * 100)}% still watching at the end`}>
          {[0, 0.5, 1].map((r) => (
            <g key={r}>
              <line className={styles.grid} x1={padL} x2={padL + plotW} y1={y(r)} y2={y(r)} />
              <text className={styles.axis} x={padL - 6} y={y(r)} dy="0.32em" textAnchor="end">{Math.round(r * 100)}%</text>
            </g>
          ))}
          <text className={styles.axis} x={padL} y={height - 4} textAnchor="start">{retentionAt(0, durationSeconds)}</text>
          <text className={styles.axis} x={padL + plotW} y={height - 4} textAnchor="end">{retentionAt(1, durationSeconds)}</text>

          <path className={styles.area} d={area} />
          {drop && (
            <line className={styles.drop} x1={x(drop.from.position)} x2={x(drop.to.position)}
                  y1={y(drop.from.retained)} y2={y(drop.to.retained)} />
          )}
          <path className={styles.line} d={line} />

          {hovered && (
            <g>
              <line className={styles.crosshair} x1={x(hovered.position)} x2={x(hovered.position)} y1={padT} y2={y(0)} />
              <circle className={styles.marker} cx={x(hovered.position)} cy={y(hovered.retained)} r={4} />
            </g>
          )}
          <rect className={styles.hit} x={padL} y={padT} width={plotW} height={plotH}
                onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
        </svg>
      )}
      {hovered && (
        <div className={styles.tooltip} aria-hidden="true"
             style={{ left: Math.min(Math.max(x(hovered.position), padL + 40), padL + plotW - 40) }}>
          <strong>{Math.round(hovered.retained * 100)}%</strong> still watching at {retentionAt(hovered.position, durationSeconds)}
        </div>
      )}
      {drop && (
        <p className={styles.note}>
          Biggest drop: {retentionAt(drop.from.position, durationSeconds)}–{retentionAt(drop.to.position, durationSeconds)}, from{" "}
          {Math.round(drop.from.retained * 100)}% to {Math.round(drop.to.retained * 100)}% still watching.
        </p>
      )}
      {/* The hiding box WRAPS the table: overflow does not clip a table box, so a hidden table's rows spilled
          ~260px below the chart and padded whatever scrolls around it with blank space (ART-313). */}
      <div className={styles.srOnly}>
        <table>
          <caption>Audience retention</caption>
          <thead><tr><th scope="col">Point in video</th><th scope="col">Still watching</th></tr></thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.position}><td>{retentionAt(p.position, durationSeconds)}</td><td>{Math.round(p.retained * 100)}%</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
